import { useCallback, useEffect, useMemo, useState } from 'react';
import { type SelectChangeEvent } from '@mui/material/Select';
import { useNavigate, useParams } from 'react-router-dom';
import { useGamesManagement } from '../../../../hooks/useGamesManagement';
import { useStates } from '../../../../hooks/useStates';
import type { CreateGameRequest, Game, GameDialogMode, GameStateMeta, UpdateGameRequest } from '../../../../types/features/games';
import type { SimpleState } from '../../../../types/locations';
import {
  buildStateMetaByCode,
  buildStatesByCountry,
  createGamesCsvContent,
  getGamesCsvFileName,
  getStateLabel,
} from '../mappers/stateGamesMappers';

const downloadFile = (content: string, fileName: string) => {
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const useStateGamesPageLogic = () => {
  const { stateId } = useParams<{ stateId: string }>();
  const navigate = useNavigate();
  const {
    games,
    loading,
    error,
    createLoading,
    updateLoading,
    loadGames,
    createGame,
    updateGame,
    clearError,
  } = useGamesManagement();
  const { groupedStates, loading: statesLoading, error: statesError, states } = useStates();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<GameDialogMode>('create');
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStateId, setSelectedStateId] = useState('');

  useEffect(() => {
    if (stateId) {
      loadGames(stateId);
    }
  }, [loadGames, stateId]);

  const statesByCountry = useMemo(() => buildStatesByCountry(groupedStates), [groupedStates]);
  const stateMetaByCode = useMemo(() => buildStateMetaByCode(groupedStates), [groupedStates]);

  const selectedStateDetails = useMemo<GameStateMeta | undefined>(
    () => (stateId ? stateMetaByCode.get(stateId) : undefined),
    [stateId, stateMetaByCode],
  );

  const stateLabel = useMemo(() => getStateLabel(stateId, selectedStateDetails), [selectedStateDetails, stateId]);

  const statesForSelectedCountry = useMemo<SimpleState[]>(
    () => (selectedCountry ? statesByCountry.get(selectedCountry) ?? [] : []),
    [selectedCountry, statesByCountry],
  );

  useEffect(() => {
    if (!stateId) {
      setSelectedCountry('');
      setSelectedStateId('');
      return;
    }

    if (selectedStateDetails) {
      setSelectedCountry(selectedStateDetails.country_code);
      setSelectedStateId(stateId);
    }
  }, [selectedStateDetails, stateId]);

  const isInvalidState = !stateId || (!statesLoading && !selectedStateDetails);

  const handleCountryChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedCountry(event.target.value);
    setSelectedStateId('');
  }, []);

  const handleStateSelect = useCallback((event: SelectChangeEvent<string>) => {
    const nextStateId = event.target.value;
    setSelectedStateId(nextStateId);

    if (nextStateId) {
      navigate(`/dashboard/games/${nextStateId}`);
    }
  }, [navigate]);

  const handleCreateGame = useCallback(() => {
    setDialogMode('create');
    setSelectedGame(null);
    setDialogOpen(true);
  }, []);

  const handleUpdateGame = useCallback((game: Game) => {
    setDialogMode('update');
    setSelectedGame(game);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogSubmit = useCallback(async (data: CreateGameRequest | UpdateGameRequest): Promise<boolean> => {
    if (dialogMode === 'create') {
      return createGame(data as CreateGameRequest);
    }

    if (!selectedGame) {
      return false;
    }

    return updateGame(selectedGame.id, data as UpdateGameRequest);
  }, [createGame, dialogMode, selectedGame, updateGame]);

  const handleDownloadCsv = useCallback(() => {
    if (!games.length) {
      return;
    }

    downloadFile(createGamesCsvContent(games), getGamesCsvFileName(stateId, selectedStateDetails));
  }, [games, selectedStateDetails, stateId]);

  return {
    games,
    loading,
    error,
    statesError,
    statesLoading,
    groupedStates,
    states,
    dialogOpen,
    dialogMode,
    selectedGame,
    selectedCountry,
    selectedCountryName: selectedStateDetails?.country_name || '',
    selectedStateId,
    selectedStateName: selectedStateDetails?.state.state_name || '',
    stateLabel,
    statesForSelectedCountry,
    isInvalidState,
    createLoading,
    updateLoading,
    clearError,
    handleCountryChange,
    handleStateSelect,
    handleCreateGame,
    handleUpdateGame,
    handleDialogClose,
    handleDialogSubmit,
    handleDownloadCsv,
  };
};
